import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, LogIn, LogOut, Coffee, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { fmtDuration, fmtDuration15, fmtTime, zoneNote } from "@/lib/format";
import { usePay } from "@/context/CompanyContext";
import { inCurrentPeriod, periodWord } from "@/lib/payPeriod";

// Large, thumb-friendly daily clock on/off card. Shown on the worker's first
// screen (My Jobs) and on the Timesheet tab.
export const ClockCard = () => {
  const [current, setCurrent] = useState(null);
  const [lastShift, setLastShift] = useState(null);
  const [weekSeconds, setWeekSeconds] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const pay = usePay();

  const load = async () => {
    try {
      const [cur, shifts] = await Promise.all([
        api.currentShift().catch(() => null),
        api.shifts().catch(() => []),
      ]);
      setCurrent(cur);
      // Sum worked time for the current pay period (company settings), including the
      // live open shift. Dedup by shift id so the open shift isn't counted twice.
      const byId = new Map();
      (shifts || []).forEach((s) => { if (s.id) byId.set(s.id, s); });
      if (cur && cur.id) byId.set(cur.id, cur);
      let total = 0;
      byId.forEach((s) => {
        if (inCurrentPeriod(s.clock_in, pay)) total += (s.worked_seconds || 0);
      });
      setWeekSeconds(total);
    }
    catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [pay.period, pay.start_day, pay.anchor, pay.timezone]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (fn, msg) => {
    setBusy(true);
    try {
      const res = await fn();
      // Capture the just-finished shift so we can show its rounded totals; clear
      // it when a fresh shift starts.
      if (res && res.is_open === false && res.clock_out) setLastShift(res);
      else if (res && res.is_open) setLastShift(null);
      toast.success(msg);
      await load();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  const onBreak = current?.status === "on_break";

  if (loading) return <div className="bg-card border border-border rounded-2xl p-6 mb-6 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="bg-card border border-border rounded-2xl p-6 mb-6" data-testid="clock-card">
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-border" data-testid="week-total">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">This pay {periodWord(pay)}</span>
        <span className="font-mono text-lg font-bold" data-testid="week-total-value">{fmtDuration15(weekSeconds)}</span>
      </div>
      {!current ? (
        <div className="text-center">
          {lastShift && (
            <div className="mb-5 rounded-xl border border-border bg-secondary/40 p-4 text-left" data-testid="last-shift-summary">
              <p className="text-xs font-semibold text-muted-foreground mb-2">Last shift · {fmtTime(lastShift.clock_in)}–{fmtTime(lastShift.clock_out)}</p>
              {zoneNote() && <p className="text-[11px] text-muted-foreground -mt-1 mb-2" data-testid="clock-zone-note">{zoneNote()}</p>}
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-xs text-muted-foreground">Worked</p><p className="font-mono text-xl font-bold" data-testid="last-shift-worked">{fmtDuration15(lastShift.worked_seconds)}</p></div>
                <div><p className="text-xs text-muted-foreground">Break</p><p className="font-mono text-xl font-bold" data-testid="last-shift-break">{fmtDuration15(lastShift.break_seconds)}</p></div>
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">Rounded to the nearest 15 minutes.</p>
            </div>
          )}
          <p className="font-display text-xl font-bold">You're clocked off</p>
          <p className="text-sm text-muted-foreground mb-4">Tap to start tracking your day.</p>
          <Button data-testid="clock-in-btn" disabled={busy} onClick={() => run(api.clockIn, "Clocked on")} className="w-full rounded-2xl bg-primary hover:bg-primary/90 text-white h-16 text-lg font-semibold">
            <LogIn className="w-6 h-6 mr-2" /> Clock on
          </Button>
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <span className={`w-2.5 h-2.5 rounded-full ${onBreak ? "bg-amber-500" : "bg-emerald-500 animate-pulse"}`} />
            <span className="font-semibold">{onBreak ? "On break" : "On shift"}</span>
            <span className="text-sm text-muted-foreground ml-auto font-mono">since {fmtTime(current.clock_in)}</span>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-5">
            <div className="rounded-xl bg-secondary/50 p-4"><p className="text-xs text-muted-foreground">Worked</p><p className="font-mono text-2xl font-bold">{fmtDuration(current.worked_seconds)}</p></div>
            <div className="rounded-xl bg-secondary/50 p-4"><p className="text-xs text-muted-foreground">Break</p><p className="font-mono text-2xl font-bold">{fmtDuration(current.break_seconds)}</p></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {onBreak ? (
              <Button data-testid="break-end-btn" disabled={busy} onClick={() => run(api.breakEnd, "Back on")} className="rounded-2xl bg-primary hover:bg-primary/90 text-white h-14 text-base font-semibold"><Play className="w-5 h-5 mr-2" /> End break</Button>
            ) : (
              <Button data-testid="break-start-btn" variant="outline" disabled={busy} onClick={() => run(api.breakStart, "On break")} className="rounded-2xl h-14 text-base font-semibold"><Coffee className="w-5 h-5 mr-2" /> Break</Button>
            )}
            <Button data-testid="clock-out-btn" disabled={busy} onClick={() => run(api.clockOut, "Clocked off")} className="rounded-2xl bg-stone-700 hover:bg-stone-800 text-white h-14 text-base font-semibold"><LogOut className="w-5 h-5 mr-2" /> Clock off</Button>
          </div>
        </div>
      )}
    </div>
  );
};
